import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ClientEquipamentService } from './service';
import ClientEquipamentDetailsView from './views/ClientEquipamentDetailsView';

function ClientEquipamentDetailsPage({ currentLanguage, match }) {
    const [equipmentBundle, setEquipmentBundle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadError, setDownloadError] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const equipmentId = useMemo(
        () => match.params.id || sessionStorage.getItem('CORE_EquipamentoSelecionado'),
        [match]
    );

    useEffect(() => {
        const routeId = match.params.id;
        if (routeId) {
            sessionStorage.setItem('CORE_EquipamentoSelecionado', routeId);
        }
    }, [match]);

    const breadcrumbItems = useMemo(
        () => [
            {
                label: currentLanguage.bepiService.shared.shared_breadcrumb_home,
                path: '/bepi/client',
            },
            {
                label: currentLanguage.bepiService.shared.shared_breadcrumb_equipments,
                path: '/bepi/client/equipaments',
            },
            {
                label: currentLanguage.bepiService.shared.shared_breadcrumb_details,
            },
        ],
        [currentLanguage]
    );

    const fetchEquipmentData = useCallback(async () => {
        if (!equipmentId) {
            setError(currentLanguage.bepiService.client.client_equip_details_id_not_found);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError('');
            setDownloadError('');
            const details = await ClientEquipamentService.getEquipmentDetails(equipmentId);
            const [photos, reports] = await Promise.all([
                ClientEquipamentService.getEquipmentPhotos(equipmentId).catch(() => []),
                ClientEquipamentService.getEquipmentReports(equipmentId).catch(() => [])
            ]);

            if (!isMountedRef.current) return;
            setEquipmentBundle({
                ...details,
                fotos: Array.isArray(photos) ? photos : details?.fotos || [],
                laudos: Array.isArray(reports) ? reports : details?.laudos || [],
            });
        } catch (_requestError) {
            if (!isMountedRef.current) return;
            setError(currentLanguage.bepiService.shared.shared_equip_form_loading_msg);
        } finally {
            if (!isMountedRef.current) return;
            setIsLoading(false);
        }
    }, [currentLanguage, equipmentId]);

    useEffect(() => {
        fetchEquipmentData();
    }, [fetchEquipmentData]);

    const handleImageClick = useCallback((imageUrl) => {
        setSelectedImage(imageUrl);
        setIsImageModalOpen(true);
    }, []);

    const handleCloseImageModal = useCallback(() => {
        setSelectedImage(null);
        setIsImageModalOpen(false);
    }, []);

    const handleDownloadReport = useCallback(
        async (fileName) => {
            if (!fileName) return;

            try {
                setDownloadError('');
                await ClientEquipamentService.downloadReport(fileName);
            } catch (_requestError) {
                if (!isMountedRef.current) return;
                setDownloadError(currentLanguage.bepiService.client.client_equip_details_download_error);
            }
        },
        [currentLanguage]
    );

    return (
        <ClientEquipamentDetailsView
            title={currentLanguage.bepiService.client.client_equip_details_item_data_title}
            breadcrumbItems={breadcrumbItems}
            isLoading={isLoading}
            error={error}
            currentLanguage={currentLanguage}
            loadingMessage={currentLanguage.bepiService.shared.shared_equip_form_loading_msg}
            emptyMessage={currentLanguage.bepiService.client.client_equip_details_not_found}
            equipment={equipmentBundle?.equipamento || null}
            photos={equipmentBundle?.fotos || []}
            reports={equipmentBundle?.laudos || []}
            selectedImage={selectedImage}
            isImageModalOpen={isImageModalOpen}
            onPhotoClick={handleImageClick}
            onCloseImageModal={handleCloseImageModal}
            onDownloadReport={handleDownloadReport}
            downloadError={downloadError}
        />
    );
}

ClientEquipamentDetailsPage.propTypes = {
    currentLanguage: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
};

export default withRouter(ClientEquipamentDetailsPage);

